import { IHttpProvider, IModule, IPromise, IQService } from "angular";
import { IModalService, IModalServiceInstance, IModalSettings } from "angular-ui-bootstrap";
import uirouter, { StateService, Ng1StateDeclaration, Rejection, RejectType, TransitionOptions, RawParams, StateOrName, TransitionPromise } from "@uirouter/angularjs";
import analytics from "@gtmhub/core/analytics/module";
import uiBootstrap from "@gtmhub/ui-bootstrap.module";
import { configureZone, Zone } from "./zone";
import { TracingService } from "./tracingService";
import { AnalyticsService } from "@gtmhub/core/analytics";
import { ITraceRootScopeService } from "./models";
import { TracingInterceptor } from "./tracingInterceptor";
import { InBrowserStatsInterceptor } from "./inBrowserStatsInterceptor";
import { getFileNameFromTemplateUrl } from ".";
import { INBROWSER_TRACING_STATS } from "@gtmhub/build";

/* eslint-disable prefer-rest-params */

/* eslint-disable @typescript-eslint/ban-types */

function configure(app: IModule): void {
  app.service("TracingService", TracingService);

  configureZone(app);

  app.run([
    "TracingService",
    (tracingService: TracingService) => {
      tracingService.initSessionID();
    },
  ]);

  // patch $state so we can trace state transitions
  app.run([
    "$rootScope",
    "$state",
    "$q",
    "$uibModal",
    "TracingService",
    "AnalyticsService",
    (
      $rootScope: ITraceRootScopeService,
      $state: StateService,
      $q: IQService,
      $uibModal: IModalService,
      tracingService: TracingService,
      analyticsService: AnalyticsService
    ) => {
      let initZone: Zone = null;
      let stateZone: Zone = null;

      const trackStateChangeError = (state: Ng1StateDeclaration, error: string): void => {
        analyticsService.track(error, {
          controller: state.controller,
          screen_name: state.name,
          url: state.url,
          params: state.params,
        });
      };

      const titleCase = (title: string): string => {
        return title.charAt(0).toUpperCase() + title.substr(1).toLowerCase();
      };

      const formatStringWithDelimiters = (str: string, delimiter = "_"): string => {
        const strArray = str.split(delimiter);
        for (let i = 0; i < strArray.length; i++) {
          strArray[i] = titleCase(strArray[i]);
        }

        return strArray.join(" ");
      };

      const parsePageName = (state: Ng1StateDeclaration): string => {
        if (state.data?.trackingPage) {
          return state.data?.trackingPage;
        }

        if (state.data?.titleKey) {
          return formatStringWithDelimiters(state.data.titleKey);
        }

        return formatStringWithDelimiters(state.name.replace("gtmhub.", ""), ".");
      };

      const trackStateChangeSuccess = (state: Ng1StateDeclaration): void => {
        if (!state.data?.disableAnalytics) {
          analyticsService.page(parsePageName(state), {
            controller: typeof state.controller === "string" ? state.controller : null,
          });
        }
      };

      const setTracingMetadata = (zone: Zone, actionName: string): void => {
        zone.metadata.screen = tracingService.getScreen();
        zone.metadata.action = tracingService.createAction(actionName);
      };

      $rootScope.traceInit = <TPromise>(fn: () => IPromise<TPromise>): IPromise<TPromise> => {
        const promise = initZone.run<IPromise<TPromise>>(fn, $rootScope);

        const initPromises = stateZone.metadata.initPromises as IPromise<unknown>[];
        initPromises.push(promise);

        return promise;
      };

      $rootScope.traceAction = <TResult>(name: string, fn: Function, parentZone?: Zone): TResult => {
        if (!parentZone) {
          parentZone = stateZone;
        }

        const zone = parentZone.fork(name);
        setTracingMetadata(zone, name);
        return zone.run(fn, $rootScope);
      };

      // patch $uibModal so we can trace opening of modals
      const oldOpen = $uibModal.open;
      $uibModal.open = function tracingOpen(options: IModalSettings): IModalServiceInstance {
        // use the template to create a name for the zone
        if (options.templateUrl) {
          const optionsTemplateUrl: unknown = options.templateUrl;
          const templateUrl: string = angular.isFunction(optionsTemplateUrl) ? (optionsTemplateUrl as Function)() : optionsTemplateUrl;
          if (templateUrl) {
            const zoneName = getFileNameFromTemplateUrl(templateUrl);
            const parentZone = stateZone || Zone.root;
            const zone = parentZone.fork(zoneName);
            setTracingMetadata(zone, zoneName);
            return zone.run<IModalServiceInstance>(oldOpen, this, arguments);
          }
        }
        return oldOpen.apply(this, arguments);
      };

      // patch $state so we can trace state transitions
      const oldTransitionTo = $state.transitionTo;
      $state.transitionTo = function tracingTransitionTo(to: StateOrName, toParams?: RawParams, options?: TransitionOptions): TransitionPromise {
        const isEqual = (currentState: string, nextState: string): boolean => currentState === nextState;

        const base = (options || {}).relative;
        const state = $state.get.call($state, to, base) as Ng1StateDeclaration;
        const stateName = state ? state.name : "<unknown>";
        const currentStateName = $state.current.name;

        tracingService.initScreen(stateName);

        stateZone = Zone.root.fork(stateName);
        // here we will collect additional init promises from child controllers / directives
        stateZone.metadata.initPromises = [];

        initZone = stateZone.fork("init");
        setTracingMetadata(initZone, "init");

        const promise = initZone.run<TransitionPromise>(oldTransitionTo, this, arguments);
        promise.then(
          (resolvedState) =>
            $q.all(Zone.current.metadata.initPromises as IPromise<unknown>[]).then(
              () => {
                if (!isEqual(currentStateName, resolvedState.name)) {
                  return trackStateChangeSuccess(resolvedState as unknown as Ng1StateDeclaration);
                }
              },
              () => {
                if (!isEqual(currentStateName, resolvedState.name)) {
                  return trackStateChangeError(resolvedState as unknown as Ng1StateDeclaration, "Webapp Init Error");
                }
              }
            ),
          (err: Rejection | undefined) => {
            const shouldTrackRejection = !!state && (!err || err.type === RejectType.ERROR);
            if (shouldTrackRejection) {
              trackStateChangeError(state, "Webapp Broken Link Hit");
            }
          }
        );
        return promise;
      };
    },
  ]);

  app.config([
    "$httpProvider",
    ($httpProvider: IHttpProvider) => {
      $httpProvider.interceptors.push(TracingInterceptor.factory());

      if (INBROWSER_TRACING_STATS) {
        $httpProvider.interceptors.push(InBrowserStatsInterceptor.factory());
      }
    },
  ]);
}

const mod = angular.module("tracing", [uirouter, analytics, uiBootstrap]);
configure(mod);
export default mod.name;
